import { useUser } from '@clerk/clerk-react';
import {
  getDemoAgentCategoryMap,
  getAvatarForAgentId,
  DEMO_ENABLED_USERS,
  isDemoOnlyAgent,
  isReportAgent,
  getCategoryForAgentId
} from "@/constants/agents";
import { HEADLESS_AGENTS } from "@/constants";
import type { Agent, AgentInstance } from "@/types/agent";

/**
 * Custom hook to handle demo agent filtering and enhancement
 * Provides reusable logic for filtering demo-only agents and enhancing with avatars
 */
export function useDemoAgentFiltering() {
  const { user, isLoaded } = useUser();

  // Check if current user should see demo features
  const shouldShowDemoFeatures = isLoaded &&
    user?.primaryEmailAddress?.emailAddress &&
    DEMO_ENABLED_USERS.includes(user.primaryEmailAddress.emailAddress);

  /**
   * Whether an agent instance passes the demo-visibility gate. Demo-only agents
   * are hidden unless the current user is demo-enabled.
   */
  const isVisibleForDemo = (agentInstance: AgentInstance): boolean => {
    if (isDemoOnlyAgent(agentInstance.agent_id)) {
      return Boolean(shouldShowDemoFeatures);
    }
    return true;
  };

  /**
   * Filter agents based on demo-only status
   * @param agents - Array of agents to filter
   * @returns Filtered array of agents
   */
  const filterAgents = <T extends Agent>(agents: T[]): T[] => {
    return agents.filter(agent => {
      if (isDemoOnlyAgent(String(agent.id))) {
        return shouldShowDemoFeatures;
      }
      return true;
    });
  };

  /**
   * Filter agent instances for the workforce: drops headless agents only.
   *
   * NOTE: report-surfaced agents (AEO, SEO, …) intentionally stay in the
   * workforce AND also appear in the Reports view — they're shown in both
   * places for now to avoid surprising users, until My Workforce is retired.
   * The Reports view is fed by `filterReportAgentInstances` below.
   * @param agentInstances - Array of agent instances to filter
   * @returns Filtered array of agent instances
   */
  const filterAgentInstances = (agentInstances: AgentInstance[] = []): AgentInstance[] => {
    return agentInstances.filter(agentInstance => {
      const shortName = agentInstance.agent?.short_name;

      // Filter out headless agents
      if (!shortName || HEADLESS_AGENTS.includes(shortName)) {
        return false;
      }

      return isVisibleForDemo(agentInstance);
    });
  };

  /**
   * Complement of `filterAgentInstances` for the Reports view: keeps only the
   * report-surfaced agent instances the user is allowed to see. Flag-gating is
   * the caller's responsibility (see `useReportEntries`).
   * @param agentInstances - Array of agent instances to filter
   * @returns Report-surfaced agent instances visible to the user
   */
  const filterReportAgentInstances = (agentInstances: AgentInstance[] = []): AgentInstance[] => {
    return agentInstances.filter(agentInstance => {
      const shortName = agentInstance.agent?.short_name;
      if (!shortName || !isReportAgent(shortName)) {
        return false;
      }
      return isVisibleForDemo(agentInstance);
    });
  };

  /**
   * Enhance agent with avatar from mapping if available
   * @param agent - Agent to enhance
   * @returns Enhanced agent with avatar
   */
  const enhanceAgentWithAvatar = <T extends Agent>(agent: T): T => {
    const avatar = getAvatarForAgentId(String(agent.id));
    return {
      ...agent,
      avatar: avatar || agent.avatar
    };
  };

  /**
   * Enhance agent instance with avatar from mapping if available
   * @param agentInstance - Agent instance to enhance
   * @returns Enhanced agent instance with avatar
   */
  const enhanceAgentInstanceWithAvatar = (agentInstance: AgentInstance): AgentInstance => {
    const avatar = getAvatarForAgentId(agentInstance.agent_id);
    return {
      ...agentInstance,
      agent: agentInstance.agent ? {
        ...agentInstance.agent,
        avatar: avatar || agentInstance.agent.avatar
      } : null
    };
  };

  /**
   * Categorize agents by category using the category map
   * @param agents - Array of agents to categorize
   * @returns Object with categories as keys and arrays of enhanced agents as values
   */
  const categorizeAgents = <T extends Agent>(agents: T[]): Record<string, T[]> => {
    return agents.reduce<Record<string, T[]>>((acc, agent) => {
      const category = getCategoryForAgentId(String(agent.id)) || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      
      const enhancedAgent = enhanceAgentWithAvatar(agent);
      acc[category].push(enhancedAgent);
      return acc;
    }, {});
  };

  /**
   * Categorize agent instances by category using the category map
   * @param agentInstances - Array of agent instances to categorize
   * @returns Object with categories as keys and arrays of enhanced agent instances as values
   */
  const categorizeAgentInstances = (agentInstances: AgentInstance[]): Record<string, AgentInstance[]> => {
    const AGENT_CATEGORY_MAP = getDemoAgentCategoryMap();
    
    return agentInstances.reduce<Record<string, AgentInstance[]>>((acc, agentInstance) => {
      const mapping = AGENT_CATEGORY_MAP.find(m => m.agent_id === agentInstance.agent_id);
      let category = mapping?.category;
      
      // Default to 'Other' if no category found
      if (!category) {
        category = 'Other';
      }
      
      if (!acc[category]) {
        acc[category] = [];
      }
      
      const enhancedAgentInstance = enhanceAgentInstanceWithAvatar(agentInstance);
      acc[category].push(enhancedAgentInstance);
      return acc;
    }, {});
  };

  return {
    shouldShowDemoFeatures,
    filterAgents,
    filterAgentInstances,
    filterReportAgentInstances,
    enhanceAgentWithAvatar,
    enhanceAgentInstanceWithAvatar,
    categorizeAgents,
    categorizeAgentInstances
  };
}
